import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AddressElement,
  CardElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import {
  Button,
  Card,
  Checkbox,
  Divider,
  Form,
  Input,
  Result,
  Space,
  Steps,
  Typography,
  message,
} from "antd";
import {
  derivePlanDisplayData,
  toPositiveInteger,
} from "../../../../../helpers/billingHelpers";
import {
  fetchCustomerProfile,
  updateCustomerProfile,
} from "../../../../../service/billing/customerService";
import {
  startPaymentSetup,
  attachPaymentMethod,
} from "../../../../../service/billing/paymentSetupService";
import {
  fetchSubscriptionDetails,
  createSubscription,
  fetchPaymentStatus,
} from "../../../../../service/billing/subscriptionService";
import TermsAndConditions from "./termsAndConditions.component";

const { Text } = Typography;

const pruneEmptyObject = (input) => {
  if (!input || typeof input !== "object") {
    return {};
  }

  return Object.entries(input).reduce((accumulator, [key, value]) => {
    if (value === null || value === undefined || value === "") {
      return accumulator;
    }

    if (Array.isArray(value)) {
      if (value.length > 0) {
        accumulator[key] = value;
      }
      return accumulator;
    }

    if (typeof value === "object") {
      const nested = pruneEmptyObject(value);
      if (Object.keys(nested).length > 0) {
        accumulator[key] = nested;
      }
      return accumulator;
    }

    accumulator[key] = value;
    return accumulator;
  }, {});
};

const normalizeStripeAddress = (address) => {
  if (!address || typeof address !== "object") {
    return null;
  }

  const normalized = pruneEmptyObject({
    line1: address?.line1,
    line2: address?.line2,
    city: address?.city,
    state: address?.state,
    postal_code: address?.postal_code,
    country: address?.country,
  });

  return Object.keys(normalized).length > 0 ? normalized : null;
};

const hasCompleteAddress = (address) =>
  Boolean(
    address &&
      address.line1 &&
      address.city &&
      address.country &&
      address.postal_code &&
      address.state
  );

const CANDIDATE_ERROR_KEYS = [
  "message",
  "error",
  "errorMessage",
  "detail",
  "description",
  "title",
  "errors",
];

const GENERIC_AXIOS_ERROR_REGEX = /^Request failed with status code \d+$/i;

const extractErrorMessage = (error, fallbackMessage) => {
  const pullMessage = (payload) => {
    if (!payload) {
      return null;
    }

    if (typeof payload === "string") {
      const trimmed = payload.trim();
      return trimmed.length > 0 ? trimmed : null;
    }

    if (Array.isArray(payload)) {
      for (const entry of payload) {
        const result = pullMessage(entry);
        if (result) {
          return result;
        }
      }
      return null;
    }

    if (typeof payload === "object") {
      for (const key of CANDIDATE_ERROR_KEYS) {
        if (Object.prototype.hasOwnProperty.call(payload, key)) {
          const result = pullMessage(payload[key]);
          if (result) {
            return result;
          }
        }
      }

      for (const value of Object.values(payload)) {
        if (typeof value === "string") {
          const trimmed = value.trim();
          if (trimmed.length > 3) {
            return trimmed;
          }
        } else if (typeof value === "object") {
          const result = pullMessage(value);
          if (result) {
            return result;
          }
        }
      }
    }

    return null;
  };

  const responseMessage =
    pullMessage(error?.response?.data) ?? pullMessage(error?.data);

  if (responseMessage) {
    return responseMessage;
  }

  if (typeof error?.message === "string") {
    const trimmed = error.message.trim();
    if (trimmed && !GENERIC_AXIOS_ERROR_REGEX.test(trimmed)) {
      return trimmed;
    }
  }

  return fallbackMessage;
};

const BillingWizard = ({
  stepItems,
  selectedPlan,
  userCount,
  formatCurrency,
  onCancel,
  onComplete,
  onReturnToStatus,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const [activeStep, setActiveStep] = useState(0);
  const [contactInfo, setContactInfo] = useState({
    email: "",
    phoneNumber: "",
  });
  const [addressComplete, setAddressComplete] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [confirmationSummary, setConfirmationSummary] = useState(null);
  const [addressDetails, setAddressDetails] = useState(null);
  const [addressElementDefaults, setAddressElementDefaults] = useState(null);
  const [setupClientSecret, setSetupClientSecret] = useState(null);
  const [savingContact, setSavingContact] = useState(false);
  const [savingBillingDetails, setSavingBillingDetails] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [openTerms, setOpenTerms] = useState(false);

  useEffect(() => {
    setActiveStep(0);
    setContactInfo({ email: "", phoneNumber: "" });
    setAddressComplete(false);
    setCardComplete(false);
    setTermsAccepted(false);
    setConfirmationSummary(null);
    setAddressDetails(null);
    setAddressElementDefaults(null);
    setSetupClientSecret(null);
    setOpenTerms(false);
  }, [selectedPlan?.id, userCount]);

  useEffect(() => {
    let cancelled = false;

    const loadCustomerProfile = async () => {
      setLoadingCustomer(true);
      try {
        const profile = await fetchCustomerProfile();
        if (cancelled) {
          return;
        }

        if (profile) {
          const resolvedEmail = profile.email ?? "";
          const resolvedPhone = profile.phone ?? "";

          setContactInfo({
            email: resolvedEmail,
            phoneNumber: resolvedPhone,
          });

          const normalizedAddress = normalizeStripeAddress(profile?.address);
          const resolvedName = profile?.name;

          if (normalizedAddress || resolvedName) {
            const defaultValues = pruneEmptyObject({
              name: resolvedName ?? undefined,
              address: normalizedAddress ?? undefined,
            });

            if (Object.keys(defaultValues).length > 0) {
              setAddressElementDefaults(defaultValues);
            }

            if (normalizedAddress) {
              const addressPayload = {
                address: normalizedAddress,
              };

              if (resolvedName) {
                addressPayload.name = resolvedName;
              }

              setAddressDetails(addressPayload);
              setAddressComplete(hasCompleteAddress(normalizedAddress));
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          message.error(
            extractErrorMessage(error, "Unable to load billing profile"),
            5
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingCustomer(false);
        }
      }
    };

    loadCustomerProfile();

    return () => {
      cancelled = true;
    };
  }, []);

  const normalizedUserCount = useMemo(
    () => toPositiveInteger(userCount, 1),
    [userCount]
  );

  const planDisplayData = useMemo(() => {
    if (!selectedPlan) {
      return null;
    }

    return derivePlanDisplayData(selectedPlan, normalizedUserCount, {
      fallbackCurrency: selectedPlan.currency,
    });
  }, [selectedPlan, normalizedUserCount]);

  const resolvedPlanId = useMemo(
    () => selectedPlan?.priceId ?? selectedPlan?.id ?? null,
    [selectedPlan]
  );

  const addressElementKey = useMemo(
    () =>
      addressElementDefaults
        ? "billing-address-preloaded"
        : "billing-address-empty",
    [addressElementDefaults]
  );

  const addressElementOptions = useMemo(() => {
    const options = {
      mode: "billing",
      allowedCountries: ["US", "CA"],
    };

    if (addressElementDefaults) {
      options.defaultValues = addressElementDefaults;
    }

    return options;
  }, [addressElementDefaults]);

  const cardElementOptions = useMemo(
    () => ({
      style: {
        base: {
          fontSize: "16px",
          color: "#111827",
          "::placeholder": {
            color: "#9ca3af",
          },
        },
      },
    }),
    []
  );

  const buildCustomerPayload = useCallback(
    (overrides = {}) => {
      const payload = pruneEmptyObject({
        email: contactInfo.email || undefined,
        phone: contactInfo.phoneNumber || undefined,
        ...overrides,
      });

      return payload;
    },
    [contactInfo.email, contactInfo.phoneNumber]
  );

  const handleNextStep = () => setActiveStep((previous) => previous + 1);
  const handlePreviousStep = () =>
    setActiveStep((previous) => Math.max(0, previous - 1));

  const handleContactContinue = async () => {
    setSavingContact(true);
    try {
      const payload = buildCustomerPayload();
      if (Object.keys(payload).length > 0) {
        await updateCustomerProfile(payload);
      }
      handleNextStep();
    } catch (error) {
      message.error(
        extractErrorMessage(error, "Unable to save contact details"),
        5
      );
    } finally {
      setSavingContact(false);
    }
  };

  const handleBillingDetailsContinue = async () => {
    if (!addressComplete) {
      return;
    }

    setSavingBillingDetails(true);
    try {
      const overrides = {};
      if (addressDetails?.address) {
        overrides.address = addressDetails.address;
      }
      if (addressDetails?.name) {
        overrides.name = addressDetails.name;
      }

      const payload = buildCustomerPayload(overrides);
      if (Object.keys(payload).length > 0) {
        await updateCustomerProfile(payload);
      }

      const setupResponse = await startPaymentSetup();
      if (!setupResponse?.clientSecret) {
        throw new Error("Unable to initiate payment setup.");
      }

      setSetupClientSecret(setupResponse.clientSecret);
      handleNextStep();
    } catch (error) {
      message.error(
        extractErrorMessage(error, "Unable to save billing details"),
        5
      );
    } finally {
      setSavingBillingDetails(false);
    }
  };

  const handleCheckout = useCallback(async () => {
    if (!stripe || !elements) {
      message.error("Payment service is not ready. Please try again.", 5);
      return;
    }

    if (!resolvedPlanId) {
      message.error("A billing plan must be selected before checkout.", 5);
      return;
    }

    setProcessingPayment(true);
    try {
      let activeSetupSecret = setupClientSecret;
      if (!activeSetupSecret) {
        const setupResponse = await startPaymentSetup();
        activeSetupSecret = setupResponse?.clientSecret;
        if (!activeSetupSecret) {
          throw new Error("Unable to initiate payment setup.");
        }
        setSetupClientSecret(activeSetupSecret);
      }

      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error("Payment information is incomplete.");
      }

      const billingDetails = pruneEmptyObject({
        email: contactInfo.email,
        phone: contactInfo.phoneNumber,
        name: addressDetails?.name,
        address: addressDetails?.address,
      });

      const setupResult = await stripe.confirmCardSetup(activeSetupSecret, {
        payment_method: {
          card: cardElement,
          billing_details: billingDetails,
        },
      });

      if (setupResult.error) {
        throw new Error(setupResult.error.message);
      }

      const paymentMethodId = setupResult?.setupIntent?.payment_method;
      if (!paymentMethodId) {
        throw new Error("Payment method was not created.");
      }

      await attachPaymentMethod(paymentMethodId);

      const subscriptionResponse = await createSubscription({
        priceId: resolvedPlanId,
        quantity: normalizedUserCount,
      });

      let { intentType, intentId, intentStatus, clientSecret } =
        subscriptionResponse;

      if (clientSecret) {
        if (
          intentType === "payment_intent" &&
          (intentStatus === "requires_action" ||
            intentStatus === "requires_confirmation")
        ) {
          // Confirm payment intent
          const confirmationResult = await stripe.confirmCardPayment(
            clientSecret
          );
          if (confirmationResult.error) {
            throw new Error(confirmationResult.error.message);
          }
          intentStatus =
            confirmationResult.paymentIntent?.status ?? intentStatus;
          clientSecret = confirmationResult.paymentIntent?.client_secret;
        } else if (
          intentType === "setup_intent" &&
          (intentStatus === "requires_action" ||
            intentStatus === "requires_confirmation")
        ) {
          // Confirm setup intent
          const confirmationResult = await stripe.confirmCardSetup(
            clientSecret
          );
          if (confirmationResult.error) {
            throw new Error(confirmationResult.error.message);
          }
          intentStatus = confirmationResult.setupIntent?.status ?? intentStatus;
          clientSecret = confirmationResult.setupIntent?.client_secret;
        }
      }

      if (
        intentType === "payment_intent" &&
        intentId &&
        intentStatus !== "succeeded"
      ) {
        const latestStatus = await fetchPaymentStatus(intentId);
        intentStatus = latestStatus?.paymentStatus ?? intentStatus;
      }

      if (intentType === "setup_intent" && intentStatus !== "succeeded") {
        throw new Error(
          `Setup authorization did not complete. Current status: ${
            intentStatus || "unknown"
          }.`
        );
      }

      if (intentStatus !== "succeeded") {
        throw new Error(
          `Subscription did not complete. Current status: ${
            intentStatus || "unknown"
          }.`
        );
      }

      const latestSubscription = await fetchSubscriptionDetails();
      if (!latestSubscription || latestSubscription.firstTime) {
        throw new Error(
          "Subscription activation is still processing. Please try again shortly."
        );
      }

      setConfirmationSummary(latestSubscription);
      setActiveStep(3);
      onComplete(latestSubscription);
    } catch (error) {
      message.error(
        extractErrorMessage(error, "Unable to complete checkout"),
        5
      );
    } finally {
      setProcessingPayment(false);
    }
  }, [
    addressDetails,
    contactInfo.email,
    contactInfo.phoneNumber,
    elements,
    normalizedUserCount,
    onComplete,
    resolvedPlanId,
    setupClientSecret,
    stripe,
  ]);

  const renderPlanSummary = () => {
    if (!selectedPlan || !planDisplayData) {
      return null;
    }

    return (
      <Card className="billing-summary-card">
        <Space direction="vertical" size="small">
          <Text strong>{selectedPlan.name} plan</Text>
          <Text type="secondary">{selectedPlan.description}</Text>
          <Divider style={{ margin: "12px 0" }} />
          {/* <Text>
            {normalizedUserCount} employees -{" "}
            {formatCurrency(
              planDisplayData.pricePerUser,
              planDisplayData.currency
            )}{" "}
            per employee
          </Text> */}
          <Text strong>
            Total:{" "}
            {formatCurrency(
              planDisplayData.totalAmount,
              planDisplayData.currency
            )}
            {" / "}
            {planDisplayData.billingCycleLabel}
          </Text>
        </Space>
      </Card>
    );
  };

  const renderContactDetailsStep = () => (
    <div className="billing-step-content">
      <Form layout="vertical">
        <Form.Item label="Phone Number" required>
          <Input
            placeholder="Enter phone number"
            value={contactInfo.phoneNumber}
            onChange={(event) =>
              setContactInfo((previous) => ({
                ...previous,
                phoneNumber: event.target.value,
              }))
            }
          />
        </Form.Item>
        <Form.Item label="Email Address" required>
          <Input
            placeholder="Enter email address"
            value={contactInfo.email}
            onChange={(event) =>
              setContactInfo((previous) => ({
                ...previous,
                email: event.target.value,
              }))
            }
          />
        </Form.Item>
      </Form>
      <Space size="middle" style={{ marginTop: 24 }}>
        <Button onClick={onCancel}>Back</Button>
        <Button
          type="primary"
          onClick={handleContactContinue}
          disabled={
            !contactInfo.email ||
            !contactInfo.phoneNumber ||
            savingContact ||
            loadingCustomer
          }
          loading={savingContact}
        >
          Continue
        </Button>
      </Space>
    </div>
  );

  const renderBillingDetailsStep = () => (
    <div className="billing-step-content">
      <div className="address-element-wrapper">
        <AddressElement
          key={addressElementKey}
          options={addressElementOptions}
          onChange={(event) => {
            setAddressComplete(event.complete);
            setAddressDetails(event.value);
          }}
        />
      </div>
      <Space size="middle" style={{ marginTop: 24 }}>
        <Button onClick={handlePreviousStep}>Back</Button>
        <Button
          type="primary"
          onClick={handleBillingDetailsContinue}
          disabled={!addressComplete || savingBillingDetails}
          loading={savingBillingDetails}
        >
          Continue
        </Button>
      </Space>
    </div>
  );

  const renderPaymentStep = () => (
    <div className="billing-step-content">
      {openTerms ? (
        <TermsAndConditions
          open={openTerms}
          onReject={() => {
            setOpenTerms(false);
            setTermsAccepted(false);
          }}
          onConfirm={() => {
            setOpenTerms(false);
            setTermsAccepted(true);
          }}
        />
      ) : (
        <></>
      )}
      <div className="card-element-wrapper">
        <CardElement
          options={cardElementOptions}
          onChange={(event) => setCardComplete(event.complete)}
        />
      </div>
      <Checkbox
        style={{ marginTop: 24 }}
        checked={termsAccepted}
        onClick={() => {
          if (!termsAccepted) {
            setOpenTerms(true);
          } else {
            setTermsAccepted(false);
          }
        }}
      >
        I have read and agree to the
        <span style={{ color: "blue", textDecoration: "underline" }}>
          {" "}
          terms and conditions
        </span>
      </Checkbox>
      <Space size="middle" style={{ marginTop: 24 }}>
        <Button onClick={handlePreviousStep} disabled={processingPayment}>
          Back
        </Button>
        <Button
          type="primary"
          onClick={handleCheckout}
          disabled={!cardComplete || !termsAccepted || processingPayment}
          loading={processingPayment}
        >
          Checkout
        </Button>
      </Space>
    </div>
  );

  const renderConfirmationStep = () => (
    <Result
      className="billing-confirmation"
      status="success"
      title="Thanks for signing up with Alpaca Systems!"
      extra={[
        <Button type="primary" key="back" onClick={onReturnToStatus}>
          Back to Billing
        </Button>,
      ]}
    >
      {confirmationSummary && (
        <Space direction="vertical" size="small">
          <Text strong>{confirmationSummary.planName} plan</Text>
          {/* <Text>
            {confirmationSummary.totalUsers} employees -{" "}
            {formatCurrency(
              confirmationSummary.pricePerUser,
              confirmationSummary.currency
            )}{" "}
            per employee
          </Text> */}
          <Text>
            Total due:{" "}
            {formatCurrency(
              confirmationSummary.totalAmount,
              confirmationSummary.currency
            )}
          </Text>
          {confirmationSummary.nextBillingDate ? (
            <Text type="secondary">
              Next charge on {confirmationSummary.nextBillingDate}
            </Text>
          ) : null}
        </Space>
      )}
    </Result>
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderContactDetailsStep();
      case 1:
        return renderBillingDetailsStep();
      case 2:
        return renderPaymentStep();
      case 3:
        return renderConfirmationStep();
      default:
        return null;
    }
  };

  if (!selectedPlan) {
    return null;
  }

  return (
    <div className="billing-wizard">
      <div className="billing-wizard-summary">{renderPlanSummary()}</div>
      <div className="billing-wizard-content">
        <div className="billing-stepper">
          <Steps size="small" current={activeStep} items={stepItems} />
        </div>
        {renderStepContent()}
      </div>
    </div>
  );
};

BillingWizard.defaultProps = {
  onCancel: () => {},
  onComplete: () => {},
  onReturnToStatus: () => {},
};

export default BillingWizard;
